import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import Dashboard from './Dashboard';
import DeliveryDispatch from './DeliveryDispatch';
import './GroceryOrdersReceived.css';

const BASE_URL = process.env.REACT_APP_API_URL || 'https://vakra.co.in';

const STATUS_FLOW = {
  new: {
    label: 'New',
    short: 'New',
    action: 'Accept',
    next: 'accepted',
    className: 'new',
  },
  accepted: {
    label: 'Accepted',
    short: 'Accepted',
    action: 'Packing',
    next: 'packing',
    className: 'accepted',
  },
  packing: {
    label: 'Packing',
    short: 'Packing',
    action: 'Ready',
    next: 'ready',
    className: 'packing',
  },
  ready: {
    label: 'Ready',
    short: 'Ready',
    action: 'Complete',
    next: 'done',
    className: 'ready',
  },
  completed: {
    label: 'Completed',
    short: 'Done',
    action: 'Completed',
    next: null,
    className: 'completed',
  },
  cancelled: {
    label: 'Cancelled',
    short: 'Cancelled',
    action: 'Cancelled',
    next: null,
    className: 'cancelled',
  },
};

const READY_REPORT_OPTIONS = [
  { key: 'today', label: 'Today', shortLabel: 'Today', days: 1 },
  { key: '2days', label: 'Last 2 Days', shortLabel: '2 Days', days: 2 },
  { key: '7days', label: 'Last 7 Days', shortLabel: '7 Days', days: 7 },
  { key: '30days', label: 'Last 30 Days', shortLabel: '30 Days', days: 30 },
  { key: 'custom', label: 'Custom Date', shortLabel: 'Custom', days: null },
];

const DEFAULT_SETTINGS = {
  allowManualItems: true,
};

const DEFAULT_PRINT_SETTINGS = {
  phoneInvoice: '',
};

const getTodayDateInput = () => new Date().toISOString().slice(0, 10);

const GroceryOrdersReceived = ({
  vendorId: externalVendorId = null,
  vendorType: externalVendorType = 'grocery',
}) => {
  const [activeOrders, setActiveOrders] = useState([]);
  const [historyOrders, setHistoryOrders] = useState([]);

  const [vendorId, setVendorId] = useState(externalVendorId);
  const [vendorType, setVendorType] = useState(externalVendorType || 'grocery');

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [groceryTypeFilter, setGroceryTypeFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('active');

  const [showReadyFilter, setShowReadyFilter] = useState(false);
  const [readyReportPreset, setReadyReportPreset] = useState('today');
  const [readyStartDate, setReadyStartDate] = useState(getTodayDateInput);
  const [readyEndDate, setReadyEndDate] = useState(getTodayDateInput);
  const [readyReport, setReadyReport] = useState({
    count: 0,
    orders: [],
    loading: false,
    source: 'frontend_fallback',
  });

  const [initialLoading, setInitialLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [currentScreen, setCurrentScreen] = useState('list');
  const [sidePanel, setSidePanel] = useState('orders');

  const [orderEdits, setOrderEdits] = useState({});

  const [invoiceState, setInvoiceState] = useState({
    paymentStatus: 'paid',
    paymentMethod: 'cash',
    discountValue: '',
    amountPaid: '',
  });

  const [vendorShop, setVendorShop] = useState({
    shopName: '',
    liveLocation: '',
    number: '',
    shopLogo: '',
    shopImage: '',
  });

  const [uiSettings, setUiSettings] = useState(() => {
    try {
      const saved = localStorage.getItem('grocery_ui_settings');
      const parsed = saved ? JSON.parse(saved) : DEFAULT_SETTINGS;
      return {
        ...DEFAULT_SETTINGS,
        ...parsed,
        allowManualItems: true,
      };
    } catch {
      return DEFAULT_SETTINGS;
    }
  });

  const [quickPrintSettings, setQuickPrintSettings] = useState(() => {
    try {
      const saved = localStorage.getItem('grocery_quick_print_settings');
      return saved ? JSON.parse(saved) : DEFAULT_PRINT_SETTINGS;
    } catch {
      return DEFAULT_PRINT_SETTINGS;
    }
  });

  const pollingRef = useRef(null);
  const requestInFlightRef = useRef(false);
  const activeSignatureRef = useRef('');
  const historySignatureRef = useRef('');

  const getToken = () => localStorage.getItem('token') || '';

  const toNumber = (value, fallback = 0) => {
    const num = Number(value);
    return Number.isFinite(num) ? num : fallback;
  };

  const safeJsonParse = (value) => {
    try {
      if (value === null || value === undefined) return null;
      if (typeof value === 'object') return value;
      if (typeof value === 'string' && !value.trim()) return null;
      return JSON.parse(value);
    } catch {
      return null;
    }
  };

  const pickFirstNonEmpty = (...values) => {
    for (const value of values) {
      if (value === null || value === undefined) continue;
      const str = String(value).trim();
      if (str) return str;
    }
    return '';
  };

  const absoluteUrl = (value) => {
    if (!value) return '';
    const str = String(value).trim();
    if (!str) return '';
    if (/^https?:\/\//i.test(str)) return str;
    if (str.startsWith('//')) return `https:${str}`;
    if (str.startsWith('/')) return `${BASE_URL}${str}`;
    return `${BASE_URL}/${str.replace(/^\/+/, '')}`;
  };

  const formatMoney = (value) =>
    `₹${Math.round(toNumber(value, 0)).toLocaleString('en-IN')}`;

  const formatDate = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);

    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const formatDateTime = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);

    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getTimeText = (createdAt) => {
    if (!createdAt) return 'Just now';

    const created = new Date(createdAt);
    if (Number.isNaN(created.getTime())) return 'Just now';

    const now = new Date();
    const diffMs = now - created;
    const mins = Math.floor(diffMs / (1000 * 60));

    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins} min`;

    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours} hr`;

    return created.toLocaleDateString('en-IN');
  };

  const getInvoiceDateText = (order) => {
    const date = order?.created_at ? new Date(order.created_at) : new Date();

    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getInvoiceNumber = (order) => {
    if (!order?.id) return '';
    return `INV-${String(order.id).padStart(6, '0')}`;
  };

  const getReadyReportOption = () =>
    READY_REPORT_OPTIONS.find((item) => item.key === readyReportPreset) ||
    READY_REPORT_OPTIONS[0];

  const getReadyReportLabel = () => {
    if (readyReportPreset === 'custom') {
      if (readyStartDate && readyEndDate) {
        return `${formatDate(readyStartDate)} - ${formatDate(readyEndDate)}`;
      }
      return 'Custom Date';
    }

    return getReadyReportOption().label;
  };

  const getReadyReportShortLabel = () => {
    if (readyReportPreset === 'custom') return 'Custom';
    return getReadyReportOption().shortLabel || 'Today';
  };

  const buildReadyReportParams = () => {
    if (readyReportPreset === 'custom') {
      return {
        startDate: readyStartDate,
        endDate: readyEndDate,
      };
    }

    return {
      days: getReadyReportOption().days || 1,
    };
  };

  const isTodayDate = (value) => {
    if (!value) return false;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return false;

    const today = new Date();

    return (
      date.getFullYear() === today.getFullYear() &&
      date.getMonth() === today.getMonth() &&
      date.getDate() === today.getDate()
    );
  };

  const isDateInsideReadyPreset = (value) => {
    if (!value) return false;

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return false;

    if (readyReportPreset === 'today') {
      return isTodayDate(value);
    }

    if (readyReportPreset === 'custom') {
      if (!readyStartDate || !readyEndDate) return false;

      const start = new Date(`${readyStartDate}T00:00:00`);
      const end = new Date(`${readyEndDate}T23:59:59`);

      if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return false;

      return date >= start && date <= end;
    }

    const days = getReadyReportOption().days || 1;
    const from = new Date();
    from.setHours(0, 0, 0, 0);
    from.setDate(from.getDate() - (days - 1));

    return date >= from;
  };

  const getFrontendReadyFallback = useCallback(() => {
    const allOrders = [...activeOrders, ...historyOrders];

    const readyOrCompleted = allOrders.filter((order) => {
      const status = order.display_status;

      // Operational Ready counter me sirf abhi ready orders count honge.
      // Delivered / completed orders History me jayenge, Ready me nahi.
      if (status !== 'ready') return false;

      const baseDate =
        order.ready_at ||
        order.readyAt ||
        order.ready_time ||
        order.readyTime ||
        order.updated_at ||
        order.created_at;

      return isDateInsideReadyPreset(baseDate);
    });

    return {
      count: readyOrCompleted.length,
      orders: readyOrCompleted.slice(0, 30).map((order) => ({
        order_id: order.id,
        id: order.id,
        customer_name: order.customer_name_normalized || 'Customer',
        customer_mobile: order.customer_mobile || order.customer_number || '',
        total_price: order.display_total || 0,
        ready_at:
          order.ready_at ||
          order.readyAt ||
          order.ready_time ||
          order.readyTime ||
          order.updated_at ||
          order.created_at,
        order_mode: order.order_mode_normalized || 'pickup',
        grocery_mode: order.grocery_mode_normalized || 'retail',
        status: order.display_status,
      })),
      source: 'frontend_fallback',
    };
  }, [activeOrders, historyOrders, readyReportPreset, readyStartDate, readyEndDate]);

  const parseProducts = useCallback((order) => {
    try {
      const raw = order?.productsParsed || order?.parsed_products || order?.products;

      if (!raw) return [];
      if (Array.isArray(raw)) return raw;
      if (typeof raw === 'string') return JSON.parse(raw);

      return [];
    } catch (err) {
      console.error('products parse error:', err);
      return [];
    }
  }, []);

  const getOrderMode = (order) => {
    const raw = String(
      order?.order_mode_normalized ||
        order?.order_mode ||
        order?.orderMode ||
        ''
    )
      .trim()
      .toLowerCase();

    if (raw === 'delivery') return 'delivery';
    if (raw === 'pickup') return 'pickup';
    if (raw === 'takeaway') return 'pickup';
    if (raw === 'dine_in') return 'pickup';
    if (raw === 'walk_in') return 'pickup';

    return 'pickup';
  };

  const getGroceryMode = (order) => {
    const raw = String(
      order?.grocery_mode_normalized ||
        order?.grocery_mode ||
        order?.groceryMode ||
        order?.mode ||
        ''
    )
      .trim()
      .toLowerCase();

    if (raw === 'wholesale') return 'wholesale';
    return 'retail';
  };

  const isOnlinePaymentVerified = (order) => {
    const method = String(
      order?.payment_method_normalized ||
        order?.payment_method ||
        order?.paymentMethod ||
        order?.payment_mode ||
        ''
    )
      .trim()
      .toLowerCase();

    const status = String(
      order?.payment_status_normalized ||
        order?.payment_status ||
        order?.paymentStatus ||
        ''
    )
      .trim()
      .toLowerCase();

    const attemptStatus = String(
      order?.payment_attempt_status ||
        order?.paymentAttemptStatus ||
        ''
    )
      .trim()
      .toLowerCase();

    const hasVerifiedAt = Boolean(order?.payment_verified_at || order?.paymentVerifiedAt);
    const hasReference = Boolean(
      order?.payment_reference_id ||
        order?.paymentReferenceId ||
        order?.razorpay_payment_id
    );

    const gatewayConfirmed =
      ['verified', 'success', 'successful', 'captured', 'paid'].includes(attemptStatus) ||
      status === 'paid';

    return (
      hasVerifiedAt ||
      (hasReference && gatewayConfirmed) ||
      (method === 'online' && status === 'paid' && hasReference)
    );
  };

  const getPaymentMethod = (order) => {
    if (isOnlinePaymentVerified(order)) return 'online';

    const raw = String(
      order?.payment_method_normalized ||
        order?.payment_method ||
        order?.paymentMethod ||
        order?.payment_mode ||
        ''
    )
      .trim()
      .toLowerCase();

    if (raw.includes('razorpay') || raw === 'online') return 'online';
    if (raw === 'upi') return 'upi';
    if (raw === 'cash') return 'cash';
    if (raw === 'cod') return 'cod';
    if (raw === 'card') return 'card';
    if (raw === 'credit') return 'credit';
    if (raw === 'other') return 'other';

    if (getOrderMode(order) === 'delivery') return 'cod';

    return 'cash';
  };

  const getPaymentLabel = (method) => {
    if (method === 'online') return 'Online';
    if (method === 'upi') return 'UPI';
    if (method === 'cod') return 'COD';
    if (method === 'cash') return 'Cash';
    if (method === 'credit') return 'Credit';
    if (method === 'card') return 'Card';
    if (method === 'other') return 'Other';
    return method || 'Cash';
  };

  const getDisplayStatus = (order) => {
    const raw = String(
      order?.display_status ||
        order?.status ||
        order?.order_status ||
        order?.new_status ||
        ''
    )
      .trim()
      .toLowerCase();

    if (raw === 'pending') return 'new';
    if (raw === 'new') return 'new';
    if (raw === 'placed') return 'new';
    if (raw === 'order_placed') return 'new';

    if (raw === 'accepted') return 'accepted';
    if (raw === 'accept') return 'accepted';
    if (raw === 'confirmed') return 'accepted';
    if (raw === 'confirm') return 'accepted';

    if (raw === 'packing') return 'packing';
    if (raw === 'preparing') return 'packing';
    if (raw === 'prepared') return 'packing';
    if (raw === 'packed') return 'packing';
    if (raw === 'packaging') return 'packing';

    if (raw === 'ready') return 'ready';
    if (raw === 'ready_for_pickup') return 'ready';
    if (raw === 'ready_to_deliver') return 'ready';
    if (raw === 'out_for_delivery') return 'ready';

    if (raw === 'done') return 'completed';
    if (raw === 'completed') return 'completed';
    if (raw === 'complete') return 'completed';
    if (raw === 'delivered') return 'completed';
    if (raw === 'closed') return 'completed';

    if (raw === 'cancelled') return 'cancelled';
    if (raw === 'canceled') return 'cancelled';
    if (raw === 'cancel') return 'cancelled';

    return 'new';
  };

  const getFullAddress = (order) => {
    if (order?.full_address) return order.full_address;

    const deliveryObj =
      typeof order?.delivery_details === 'string'
        ? safeJsonParse(order.delivery_details)
        : order?.delivery_details || null;

    return [
      deliveryObj?.address_line1 || deliveryObj?.addressLine1 || order?.address_line1,
      deliveryObj?.address_line2 || deliveryObj?.addressLine2 || order?.address_line2,
      deliveryObj?.landmark || order?.landmark,
      deliveryObj?.city || order?.city,
      deliveryObj?.state || order?.state,
      deliveryObj?.pincode || order?.pincode,
    ]
      .filter(Boolean)
      .join(', ');
  };

  const getDeliveryCoordinate = (order, type) => {
    const deliveryObj =
      typeof order?.delivery_details === 'string'
        ? safeJsonParse(order.delivery_details)
        : order?.delivery_details || null;

    if (type === 'latitude') {
      return (
        deliveryObj?.latitude ||
        deliveryObj?.customer_latitude ||
        order?.latitude ||
        order?.customer_latitude ||
        null
      );
    }

    return (
      deliveryObj?.longitude ||
      deliveryObj?.customer_longitude ||
      order?.longitude ||
      order?.customer_longitude ||
      null
    );
  };

  const getCustomerMapUrl = (order) => {
    const latitude = getDeliveryCoordinate(order, 'latitude');
    const longitude = getDeliveryCoordinate(order, 'longitude');

    if (latitude && longitude) {
      return `https://www.google.com/maps?q=${encodeURIComponent(`${latitude},${longitude}`)}`;
    }

    const address = getFullAddress(order);
    return address
      ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`
      : '';
  };

  const openDeliveryDispatch = () => {
    setSidePanel('delivery');
    setCurrentScreen('list');
  };

  const openOrdersPanel = ({ tab = 'active', status = 'all' } = {}) => {
    setSidePanel('orders');
    setActiveTab(tab);
    setStatusFilter(status);
    setCurrentScreen('list');
  };

  const openInvoicePanel = () => {
    if (!selectedOrderId) {
      setMessage('Invoice open karne ke liye pehle order select karein.');
      return;
    }

    setSidePanel('orders');
    setCurrentScreen('invoice');
  };

  const normalizeOrder = useCallback(
    (order) => {
      const products = parseProducts(order);

      const totalItems =
        toNumber(order?.item_count, 0) ||
        products.reduce(
          (sum, item) => sum + toNumber(item?.quantity || item?.qty || item?.packed_qty, 0),
          0
        );

      const productsTotal = products.reduce((sum, item) => {
        const qty = toNumber(item?.quantity || item?.qty || item?.packed_qty, 0);
        const rate = toNumber(
          item?.rate ?? item?.price ?? item?.selling_price ?? item?.unit_price,
          0
        );
        const lineTotal = toNumber(item?.line_total, qty * rate);
        return sum + lineTotal;
      }, 0);

      return {
        ...order,
        productsParsed: products,
        item_count: totalItems,

        customer_name_normalized:
          order?.customer_name_normalized ||
          order?.customer_name ||
          order?.name ||
          order?.delivery_name ||
          'Customer',

        customer_mobile:
          order?.customer_mobile || order?.mobile || order?.customer_number || '',

        payment_method_normalized: getPaymentMethod(order),
        grocery_mode_normalized: getGroceryMode(order),
        order_mode_normalized: getOrderMode(order),
        display_status: getDisplayStatus(order),
        full_address: getFullAddress(order),

        display_total: toNumber(
          order?.display_total ??
            order?.final_total ??
            order?.bill_total ??
            order?.total_price,
          productsTotal
        ),
      };
    },
    [parseProducts]
  );

  const buildOrderSignature = (orders) =>
    JSON.stringify(
      orders.map((order) => ({
        id: order.id,
        status: order.display_status,
        total: order.display_total,
        item_count: order.item_count,
        created_at: order.created_at,
        updated_at: order.updated_at,
        ready_at: order.ready_at || order.readyAt || order.ready_time,
        payment: order.payment_method_normalized,
        grocery_mode: order.grocery_mode_normalized,
        customer: order.customer_name_normalized,
      }))
    );

  const buildInitialEditState = useCallback((order) => {
    return {
      items: (order.productsParsed || []).map((item, index) => ({
        id: item?.id || item?.product_id || item?.productId || `item_${index}`,
        product_id: item?.product_id || item?.productId || item?.id || null,
        item_name: item?.item_name || item?.name || item?.product_name || 'Product',
        unit_label: item?.unit_label || item?.unitLabel || '',
        image: item?.image || item?.product_image || item?.image_url || '',
        quantity: toNumber(item?.quantity || item?.qty || item?.packed_qty, 1),
        rate: toNumber(
          item?.rate ?? item?.price ?? item?.selling_price ?? item?.unit_price,
          0
        ),
        removed: false,
        sourceType: item?.sourceType || item?.source_type || 'ordered',
        gstPercent: toNumber(item?.gstPercent ?? item?.gst_percent, 0),
      })),
      manualItems: [],
      summaryText: '',
      deliveryCharge: String(toNumber(order?.delivery_charge, 0) || ''),
      packingCharge: '',
      gstPercent: '',
      roundOff: '',
      manualDiscount: '',
    };
  }, []);

  const fetchVendorIfNeeded = useCallback(async () => {
    if (externalVendorId && externalVendorType) {
      setVendorId(externalVendorId);
      setVendorType(externalVendorType);
      return;
    }

    const token = getToken();

    if (!token) {
      setMessage('Please login to view grocery orders.');
      setInitialLoading(false);
      return;
    }

    try {
      const res = await axios.get(`${BASE_URL}/api/products/get-vendor-id`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setVendorId(res?.data?.vendorId ?? null);
      setVendorType(String(res?.data?.vendorType || 'grocery').toLowerCase());
    } catch (err) {
      setMessage(
        err?.response?.data?.message ||
          err?.message ||
          'Failed to fetch vendor details.'
      );
      setInitialLoading(false);
    }
  }, [externalVendorId, externalVendorType]);

  const fetchVendorShopProfile = useCallback(async () => {
    if (!vendorId) return;

    const token = getToken();

    try {
      const res = await axios.get(`${BASE_URL}/api/grocery-orders/vendor-shop-profile`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      const profile = res?.data?.vendor || null;
      if (!profile) return;

      setVendorShop({
        shopName: pickFirstNonEmpty(profile.shopName, profile.shop_name, profile.name),
        liveLocation: pickFirstNonEmpty(
          profile.liveLocation,
          profile.live_location,
          profile.address
        ),
        number: pickFirstNonEmpty(
          profile.number,
          profile.phone,
          profile.mobile,
          profile.contact_number
        ),
        shopLogo: absoluteUrl(pickFirstNonEmpty(profile.shopLogo, profile.shop_logo)),
        shopImage: absoluteUrl(pickFirstNonEmpty(profile.shopImage, profile.shop_image)),
      });
    } catch {
      // silent
    }
  }, [vendorId]);

  const fetchReadyReport = useCallback(
    async ({ silent = true } = {}) => {
      const token = getToken();

      if (!token || !vendorId) {
        const fallback = getFrontendReadyFallback();
        setReadyReport({
          ...fallback,
          loading: false,
        });
        return;
      }

      try {
        if (!silent) {
          setReadyReport((prev) => ({
            ...prev,
            loading: true,
          }));
        }

        const params = buildReadyReportParams();

        const res = await axios.get(`${BASE_URL}/api/grocery-orders/ready-report`, {
          headers: { Authorization: `Bearer ${token}` },
          params,
        });

        if (res?.data?.success) {
          const orders = Array.isArray(res.data.orders) ? res.data.orders : [];

          setReadyReport({
            count: toNumber(res.data.count, orders.length),
            orders,
            loading: false,
            source: 'backend',
          });

          return;
        }

        const fallback = getFrontendReadyFallback();
        setReadyReport({
          ...fallback,
          loading: false,
        });
      } catch (err) {
        console.warn('ready-report backend not available yet, using frontend fallback:', err?.message);

        const fallback = getFrontendReadyFallback();
        setReadyReport({
          ...fallback,
          loading: false,
        });
      }
    },
    [
      vendorId,
      readyReportPreset,
      readyStartDate,
      readyEndDate,
      activeOrders,
      historyOrders,
      getFrontendReadyFallback,
    ]
  );

  const fetchOrders = useCallback(
    async ({ silent = false } = {}) => {
      const token = getToken();

      if (!token || !vendorId || requestInFlightRef.current) return;

      requestInFlightRef.current = true;

      try {
        if (!silent) setInitialLoading(true);

        const [activeRes, historyRes] = await Promise.all([
          axios.get(`${BASE_URL}/api/grocery-orders/today-orders`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`${BASE_URL}/api/grocery-orders/history`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);

        const activeRaw = Array.isArray(activeRes?.data?.orders)
          ? activeRes.data.orders
          : [];

        const historyRaw = Array.isArray(historyRes?.data?.orders)
          ? historyRes.data.orders
          : [];

        const normalizedActive = activeRaw
          .map((order) => normalizeOrder(order))
          .filter((order) => !['completed', 'cancelled'].includes(order.display_status));

        const normalizedHistory = historyRaw.map((order) => normalizeOrder(order));

        const newActiveSignature = buildOrderSignature(normalizedActive);
        const newHistorySignature = buildOrderSignature(normalizedHistory);

        if (activeSignatureRef.current !== newActiveSignature) {
          activeSignatureRef.current = newActiveSignature;
          setActiveOrders(normalizedActive);
        }

        if (historySignatureRef.current !== newHistorySignature) {
          historySignatureRef.current = newHistorySignature;
          setHistoryOrders(normalizedHistory);
        }

        setOrderEdits((prev) => {
          const next = { ...prev };

          [...normalizedActive, ...normalizedHistory].forEach((order) => {
            if (!next[order.id]) {
              next[order.id] = buildInitialEditState(order);
            }
          });

          return next;
        });

        setSelectedOrderId((prev) => {
          const all = [...normalizedActive, ...normalizedHistory];

          if (!prev && all.length > 0) return all[0].id;

          const exists = all.some((order) => Number(order.id) === Number(prev));
          return exists ? prev : all[0]?.id || null;
        });

        if (normalizedActive.length === 0 && normalizedHistory.length === 0) {
          setMessage('No grocery orders found.');
        } else {
          setMessage((prev) => (prev === 'No grocery orders found.' ? '' : prev));
        }
      } catch (err) {
        console.error('fetchOrders error:', err);

        if (!silent) {
          setMessage(
            err?.response?.data?.message ||
              err?.message ||
              'Failed to load grocery orders.'
          );
        }
      } finally {
        requestInFlightRef.current = false;
        setInitialLoading(false);
      }
    },
    [vendorId, normalizeOrder, buildInitialEditState]
  );

  useEffect(() => {
    localStorage.setItem('grocery_ui_settings', JSON.stringify({
      ...uiSettings,
      allowManualItems: true,
    }));
  }, [uiSettings]);

  useEffect(() => {
    localStorage.setItem(
      'grocery_quick_print_settings',
      JSON.stringify(quickPrintSettings)
    );
  }, [quickPrintSettings]);

  useEffect(() => {
    setUiSettings((prev) => ({
      ...DEFAULT_SETTINGS,
      ...prev,
      allowManualItems: true,
    }));
  }, []);

  useEffect(() => {
    fetchVendorIfNeeded();
  }, [fetchVendorIfNeeded]);

  useEffect(() => {
    if (!vendorId) return;

    fetchOrders({ silent: false });
    fetchVendorShopProfile();
  }, [vendorId, fetchOrders, fetchVendorShopProfile]);

  useEffect(() => {
    if (!vendorId) return;

    fetchReadyReport({ silent: true });
  }, [vendorId, readyReportPreset, readyStartDate, readyEndDate, fetchReadyReport]);

  useEffect(() => {
    if (!vendorId) return;

    pollingRef.current = setInterval(() => {
      if (document.hidden || currentScreen !== 'list') return;
      fetchOrders({ silent: true });
      fetchReadyReport({ silent: true });
    }, 5000);

    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, [vendorId, currentScreen, fetchOrders, fetchReadyReport]);

  useEffect(() => {
    const handleReceiptDone = (event) => {
      if (event?.data?.type === 'GROCERY_RECEIPT_PRINT_DONE') {
        setCurrentScreen('list');
        setMessage('Receipt print completed.');
      }
    };

    window.addEventListener('message', handleReceiptDone);
    return () => window.removeEventListener('message', handleReceiptDone);
  }, []);

  const selectedOrder = useMemo(() => {
    return (
      [...activeOrders, ...historyOrders].find(
        (order) => Number(order.id) === Number(selectedOrderId)
      ) || null
    );
  }, [activeOrders, historyOrders, selectedOrderId]);

  const selectedEdit = selectedOrder ? orderEdits[selectedOrder.id] : null;
  const isCompletedOrder = selectedOrder?.display_status === 'completed';

  const visibleOrders = useMemo(() => {
    const base = activeTab === 'active' ? activeOrders : historyOrders;
    const q = search.trim().toLowerCase();

    return base.filter((order) => {
      const statusOk = statusFilter === 'all' || order.display_status === statusFilter;
      const typeOk =
        groceryTypeFilter === 'all' ||
        order.grocery_mode_normalized === groceryTypeFilter;

      const searchText = [
        order.id,
        order.customer_name_normalized,
        order.customer_mobile,
        order.customer_number,
        order.full_address,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      const searchOk = !q || searchText.includes(q);

      return statusOk && typeOk && searchOk;
    });
  }, [activeTab, activeOrders, historyOrders, search, statusFilter, groceryTypeFilter]);

  const statusCounts = useMemo(() => {
    const counts = {
      new: 0,
      accepted: 0,
      packing: 0,
      ready: 0,
    };

    activeOrders.forEach((order) => {
      if (counts[order.display_status] !== undefined) {
        counts[order.display_status] += 1;
      }
    });

    return counts;
  }, [activeOrders]);

  const updateOrderEdit = (orderId, updater) => {
    const matchedOrder = [...activeOrders, ...historyOrders].find(
      (order) => Number(order.id) === Number(orderId)
    );

    if (matchedOrder?.display_status === 'completed') return;

    setOrderEdits((prev) => {
      const current = prev[orderId];

      if (!current) return prev;

      return {
        ...prev,
        [orderId]: typeof updater === 'function' ? updater(current) : updater,
      };
    });
  };

  const changeItemField = (orderId, index, field, value) => {
    updateOrderEdit(orderId, (prev) => {
      const items = [...(prev.items || [])];

      items[index] = {
        ...items[index],
        [field]: value,
      };

      return { ...prev, items };
    });
  };

  const changeQuantityByStep = (orderId, index, step) => {
    updateOrderEdit(orderId, (prev) => {
      const items = [...(prev.items || [])];
      const currentQty = toNumber(items[index]?.quantity, 0);

      items[index] = {
        ...items[index],
        quantity: Math.max(0, currentQty + step),
      };

      return { ...prev, items };
    });
  };

  const toggleRemoveItem = (orderId, index) => {
    updateOrderEdit(orderId, (prev) => {
      const items = [...(prev.items || [])];

      items[index] = {
        ...items[index],
        removed: !items[index].removed,
      };

      return { ...prev, items };
    });
  };

  const changeOrderField = (orderId, field, value) => {
    updateOrderEdit(orderId, (prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const addManualItem = (orderId) => {
    updateOrderEdit(orderId, (prev) => ({
      ...prev,
      manualItems: [
        ...(prev.manualItems || []),
        {
          id: `manual_${Date.now()}`,
          product_id: null,
          item_name: '',
          unit_label: '',
          image: '',
          quantity: 1,
          rate: 0,
          removed: false,
          sourceType: 'manual',
          gstPercent: 0,
        },
      ],
    }));
  };

  const changeManualItemField = (orderId, index, field, value) => {
    updateOrderEdit(orderId, (prev) => {
      const manualItems = [...(prev.manualItems || [])];

      manualItems[index] = {
        ...manualItems[index],
        [field]: value,
      };

      return { ...prev, manualItems };
    });
  };

  const changeManualQuantityByStep = (orderId, index, step) => {
    updateOrderEdit(orderId, (prev) => {
      const manualItems = [...(prev.manualItems || [])];
      const currentQty = toNumber(manualItems[index]?.quantity, 0);

      manualItems[index] = {
        ...manualItems[index],
        quantity: Math.max(0, currentQty + step),
      };

      return { ...prev, manualItems };
    });
  };

  const removeManualItem = (orderId, index) => {
    updateOrderEdit(orderId, (prev) => {
      const manualItems = [...(prev.manualItems || [])];
      manualItems.splice(index, 1);
      return { ...prev, manualItems };
    });
  };

  const calculateSummary = (edit, discountOverride = null) => {
    if (!edit) {
      return {
        subtotal: 0,
        discount: 0,
        taxableAmount: 0,
        deliveryCharge: 0,
        packingCharge: 0,
        gstPercent: 0,
        gstAmount: 0,
        roundOff: 0,
        finalTotal: 0,
        items: [],
      };
    }

    const activeItems = [
      ...(edit.items || []).filter((item) => !item.removed),
      ...(edit.manualItems || []).filter(
        (item) => !item.removed && String(item.item_name || '').trim()
      ),
    ];

    const subtotal = activeItems.reduce((sum, item) => {
      const qty = toNumber(item.quantity, 0);
      const rate = toNumber(item.rate, 0);
      return sum + qty * rate;
    }, 0);

    const discount =
      discountOverride !== null
        ? toNumber(discountOverride, 0)
        : toNumber(edit.manualDiscount, 0);

    const taxableAmount = Math.max(0, subtotal - discount);
    const deliveryCharge = toNumber(edit.deliveryCharge, 0);
    const packingCharge = toNumber(edit.packingCharge, 0);
    const gstPercent = toNumber(edit.gstPercent, 0);
    const gstAmount = (taxableAmount * gstPercent) / 100;
    const roundOff = toNumber(edit.roundOff, 0);
    const finalTotal =
      taxableAmount + gstAmount + deliveryCharge + packingCharge + roundOff;

    return {
      subtotal,
      discount,
      taxableAmount,
      deliveryCharge,
      packingCharge,
      gstPercent,
      gstAmount,
      roundOff,
      finalTotal,
      items: activeItems,
    };
  };

  const billSummary = useMemo(() => calculateSummary(selectedEdit), [selectedEdit]);

  const invoiceSummary = useMemo(
    () => calculateSummary(selectedEdit, invoiceState.discountValue),
    [selectedEdit, invoiceState.discountValue]
  );

  const getDefaultInvoiceState = (order, edit) => {
    const summary = calculateSummary(edit);
    const isDeliveryOrder = getOrderMode(order) === 'delivery';
    const onlineVerified = isOnlinePaymentVerified(order);

    const rawStatus = String(
      order?.payment_status_normalized ||
        order?.payment_status ||
        ''
    )
      .trim()
      .toLowerCase();

    let paymentMethod = getPaymentMethod(order);
    let paymentStatus = 'unpaid';
    let amountPaid = '';

    /*
      Grocery delivery payment rules:
      1. Verified online payment always remains Online + Paid.
      2. Vendor saved Credit / Partial must remain exactly as saved.
      3. New delivery order default remains COD pending until token verification.
      4. Pickup existing behavior remains unchanged.
    */
    if (onlineVerified) {
      paymentMethod = 'online';
      paymentStatus = 'paid';
      amountPaid = String(Math.round(summary.finalTotal));
    } else if (rawStatus === 'partial') {
      paymentStatus = 'partial';
      amountPaid = String(
        toNumber(order?.amount_paid ?? order?.amountPaid, 0) || ''
      );
    } else if (paymentMethod === 'credit') {
      paymentStatus = 'unpaid';
      amountPaid = '';
    } else if (rawStatus === 'paid') {
      paymentStatus = 'paid';
      amountPaid = String(Math.round(summary.finalTotal));
    } else if (isDeliveryOrder) {
      paymentMethod = ['cash', 'cod'].includes(paymentMethod) ? 'cod' : paymentMethod;
      paymentStatus = 'unpaid';
      amountPaid = '';
    } else {
      paymentStatus = rawStatus === 'unpaid' ? 'unpaid' : 'paid';
      amountPaid =
        paymentStatus === 'paid' ? String(Math.round(summary.finalTotal)) : '';
    }

    return {
      paymentStatus,
      paymentMethod,
      discountValue: String(toNumber(edit?.manualDiscount, 0) || ''),
      amountPaid,
    };
  };

  const syncOrderStatus = async (orderId, status, successMsg) => {
    try {
      await axios.post(
        `${BASE_URL}/api/grocery-orders/update-status`,
        { orderId, status },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );

      await fetchOrders({ silent: true });
      await fetchReadyReport({ silent: true });
      setMessage(successMsg);
    } catch (err) {
      console.error('syncOrderStatus error:', err);
      setMessage(
        err?.response?.data?.message || err?.message || 'Failed to update order status.'
      );
    }
  };

  const handleCardAction = async (order) => {
    if (!order) return;

    const status = order.display_status;
    const next = STATUS_FLOW[status]?.next;

    if (!next) return;

    if (status === 'new') {
      await syncOrderStatus(order.id, next, 'Order accepted.');
      return;
    }

    if (status === 'accepted') {
      await syncOrderStatus(order.id, next, 'Order packing me chala gaya.');
      return;
    }

    if (status === 'packing') {
      await syncOrderStatus(order.id, next, 'Order ready ho gaya.');
      return;
    }

    if (status === 'ready') {
      if (order.order_mode_normalized === 'delivery') {
        setSelectedOrderId(order.id);
        setMessage(
          'Delivery order ready hai. Isko direct complete na karein. Delivery Management me rider assign karein.'
        );
        return;
      }

      await syncOrderStatus(order.id, next, 'Order completed.');
      setCurrentScreen('list');
    }
  };

  const buildSaveBillPayloadForOrder = (order, edit, overrides = {}) => {
    if (!order || !edit) return null;

    const summary = calculateSummary(
      edit,
      overrides.manualDiscount !== undefined
        ? overrides.manualDiscount
        : edit.manualDiscount
    );

    const paymentStatus = String(
      overrides.paymentStatus ||
        order.payment_status_normalized ||
        order.payment_status ||
        'unpaid'
    )
      .trim()
      .toLowerCase();

    let paymentMethod = String(
      overrides.paymentMethod ||
        order.payment_method_normalized ||
        order.payment_method ||
        'cash'
    )
      .trim()
      .toLowerCase();

    if (paymentStatus === 'unpaid') {
      const isDeliveryOrder = getOrderMode(order) === 'delivery';

      if (isDeliveryOrder && ['cash', 'cod'].includes(paymentMethod)) {
        paymentMethod = 'cod';
      } else if (!isDeliveryOrder) {
        paymentMethod = 'credit';
      }
    }

    const amountPaid =
      overrides.amountPaid !== undefined
        ? toNumber(overrides.amountPaid, 0)
        : paymentStatus === 'paid'
        ? summary.finalTotal
        : 0;

    const safeItems = (edit.items || []).map((item, index) => {
      const quantity = toNumber(item.quantity, 0);
      const rate = toNumber(item.rate, 0);
      const removed = item.removed ? 1 : 0;
      const gstPercent = toNumber(item.gstPercent ?? item.gst_percent, 0);

      return {
        ...item,
        id: item.id || item.product_id || item.productId || `item_${index}`,
        product_id: item.product_id || item.productId || item.id || null,
        item_name: item.item_name || item.name || item.product_name || 'Product',
        sourceType: item.sourceType || item.source_type || 'ordered',
        source_type: item.source_type || item.sourceType || 'ordered',
        quantity,
        packedQty: quantity,
        packed_qty: quantity,
        rate,
        gstPercent,
        gst_percent: gstPercent,
        removed,
        line_total: removed ? 0 : quantity * rate,
      };
    });

    const safeManualItems = (edit.manualItems || [])
      .filter((item) => String(item.item_name || item.name || '').trim())
      .map((item, index) => {
        const quantity = toNumber(item.quantity, 0);
        const rate = toNumber(item.rate, 0);
        const gstPercent = toNumber(item.gstPercent ?? item.gst_percent, 0);

        return {
          ...item,
          id: item.id || `manual_${index}_${Date.now()}`,
          product_id: item.product_id || item.productId || null,
          item_name: item.item_name || item.name || 'Manual Item',
          sourceType: item.sourceType || item.source_type || 'manual',
          source_type: item.source_type || item.sourceType || 'manual',
          quantity,
          packedQty: quantity,
          packed_qty: quantity,
          rate,
          gstPercent,
          gst_percent: gstPercent,
          removed: 0,
          line_total: quantity * rate,
        };
      });

    return {
      orderId: order.id,
      groceryMode: order.grocery_mode_normalized || 'retail',
      orderMode: order.order_mode_normalized || 'pickup',

      customerName:
        order.customer_name_normalized ||
        order.customer_name ||
        order.name ||
        'Customer',

      customerMobile:
        order.customer_mobile ||
        order.mobile ||
        order.customer_number ||
        '',

      customerType: order.grocery_mode_normalized || 'retail',

      paymentMethod,
      paymentStatus,

      manualDiscount: summary.discount,
      offerDiscount: 0,
      deliveryCharge: summary.deliveryCharge,
      packingCharge: summary.packingCharge,
      gstPercent: summary.gstPercent,
      roundOff: summary.roundOff,

      items: safeItems,
      manualItems: safeManualItems,

      amountPaid,
      preserveSavedPayment: Boolean(overrides.preserveSavedPayment),
      sendToDelivery: Boolean(overrides.sendToDelivery),
      notes: edit.summaryText || '',
      status: overrides.status || 'draft',
    };
  };

  const buildSaveBillPayload = (overrides = {}) => {
    return buildSaveBillPayloadForOrder(selectedOrder, selectedEdit, overrides);
  };


  const handleUpdateOrderEdits = async () => {
    if (!selectedOrder || !selectedEdit) return;

    if (isCompletedOrder) {
      setMessage('Completed order view only hai. Aap invoice print kar sakte hain.');
      return;
    }

    try {
      const payload = buildSaveBillPayload({
        status: 'draft',
        paymentStatus:
          selectedOrder.payment_status_normalized ||
          selectedOrder.payment_status ||
          'unpaid',
        paymentMethod:
          selectedOrder.payment_method_normalized ||
          selectedOrder.payment_method ||
          'cash',
      });

      if (!payload) return;

      const res = await axios.post(`${BASE_URL}/api/grocery-orders/save-bill`, payload, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });

      if (res?.data?.success) {
        setMessage('Changes saved successfully.');
        await fetchOrders({ silent: true });
        await fetchReadyReport({ silent: true });
      } else {
        setMessage(res?.data?.message || 'Changes save nahi hua.');
      }
    } catch (err) {
      console.error('handleUpdateOrderEdits error:', err);
      setMessage(
        err?.response?.data?.message ||
          err?.message ||
          'Failed to save changes.'
      );
    }
  };

  const markOrderCompleteIfNeeded = async () => {
    if (!selectedOrder) return;

    if (selectedOrder.display_status === 'completed') {
      return;
    }

    try {
      await axios.post(
        `${BASE_URL}/api/grocery-orders/update-status`,
        {
          orderId: selectedOrder.id,
          status: 'done',
        },
        {
          headers: { Authorization: `Bearer ${getToken()}` },
        }
      );
    } catch (err) {
      console.error('markOrderCompleteIfNeeded error:', err);
      throw err;
    }
  };

  const saveDeliveryDraftAndOpenDispatch = async (order) => {
    if (!order?.id) return false;

    const edit = orderEdits[order.id] || buildInitialEditState(order);

    const payload = buildSaveBillPayloadForOrder(order, edit, {
      /*
        Direct Send Delivery:
        - Existing vendor-saved Credit / Partial preserve hoga.
        - Verified Online payment preserve hogi.
        - New delivery order backend par COD pending बनेगा.
      */
      status: 'draft',
      preserveSavedPayment: true,
      sendToDelivery: true,
    });

    if (!payload) {
      setMessage('Delivery invoice draft create nahi hua.');
      return false;
    }

    try {
      const res = await axios.post(`${BASE_URL}/api/grocery-orders/save-bill`, payload, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });

      if (!res?.data?.success) {
        setMessage(res?.data?.message || 'Delivery invoice draft save nahi hua.');
        return false;
      }

      setSelectedOrderId(order.id);
      setMessage('Delivery invoice draft saved. Ab delivery boy assign karein.');

      // Save successful hote hi delivery panel open karein.
      // Refresh background me chalega, navigation ko block nahi karega.
      openDeliveryDispatch();

      await Promise.allSettled([
        fetchOrders({ silent: true }),
        fetchReadyReport({ silent: true }),
      ]);

      return true;
    } catch (err) {
      console.error('saveDeliveryDraftAndOpenDispatch error:', err);
      setMessage(
        err?.response?.data?.message ||
          err?.message ||
          'Delivery invoice draft save nahi hua.'
      );
      return false;
    }
  };

  const handleSaveInvoice = async ({
    shouldPrint = false,
    shouldComplete = true,
    openDeliveryAfterSave = false,
    sendToDelivery = false,
  } = {}) => {
    if (!selectedOrder || !selectedEdit) return false;

    const isDeliveryOrder =
      selectedOrder.order_mode_normalized === 'delivery' &&
      selectedOrder.display_status !== 'completed';

    if (shouldComplete && isDeliveryOrder) {
      setMessage(
        'Delivery order direct complete nahi hoga. Invoice save karke Delivery Management me rider assign karein.'
      );
      return false;
    }

    try {
      let paymentStatus = String(invoiceState.paymentStatus || 'paid').toLowerCase();
      let paymentMethod = String(invoiceState.paymentMethod || 'cash').toLowerCase();
      let amountPaid = 0;

      if (paymentStatus === 'unpaid') {
        if (isDeliveryOrder && ['cash', 'cod'].includes(paymentMethod)) {
          paymentMethod = 'cod';
        } else {
          paymentMethod = 'credit';
        }

        amountPaid = 0;
      } else if (paymentStatus === 'paid') {
        amountPaid = invoiceSummary.finalTotal;
      } else if (paymentStatus === 'partial') {
        amountPaid = toNumber(invoiceState.amountPaid, 0);

        if (amountPaid <= 0) {
          setMessage('Partial payment amount डालें.');
          return false;
        }

        if (amountPaid >= invoiceSummary.finalTotal) {
          amountPaid = invoiceSummary.finalTotal;
          paymentStatus = 'paid';
        }
      }

      const payload = buildSaveBillPayload({
        paymentMethod,
        paymentStatus,
        amountPaid,
        manualDiscount: toNumber(invoiceState.discountValue, 0),

        // Delivery order ko rider assign hone se pehle completed bill mat banayein.
        // Save + Print + Send Delivery invoice draft save karke Ready queue bhi banayega.
        status: shouldComplete ? 'completed' : 'draft',
        sendToDelivery,
      });

      if (!payload) return false;

      const res = await axios.post(`${BASE_URL}/api/grocery-orders/save-bill`, payload, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });

      if (!res?.data?.success) {
        setMessage(res?.data?.message || 'Invoice save nahi hua.');
        return false;
      }

      if (shouldComplete) {
        await markOrderCompleteIfNeeded();
      }

      if (shouldPrint) {
        handlePrintReceipt();
      }

      setMessage(
        shouldComplete
          ? 'Invoice saved and order completed successfully.'
          : sendToDelivery
            ? 'Invoice saved, print opened aur order Delivery Management me Ready hai.'
            : 'Invoice draft saved. Token verify hone ke baad COD payment paid hogi.'
      );

      if (openDeliveryAfterSave) {
        // Navigation first: user ko turant Delivery Management page dikhega.
        openDeliveryDispatch();
      } else {
        setCurrentScreen('list');
      }

      await Promise.allSettled([
        fetchOrders({ silent: true }),
        fetchReadyReport({ silent: true }),
      ]);

      return true;
    } catch (err) {
      console.error('handleSaveInvoice error:', err);
      setMessage(
        err?.response?.data?.message ||
          err?.message ||
          'Failed to save invoice.'
      );
      return false;
    }
  };

  const handleSavePrintComplete = async () => {
    await handleSaveInvoice({
      shouldPrint: true,
      shouldComplete: true,
    });
  };

  const handleSavePrintSendDelivery = async () => {
    await handleSaveInvoice({
      shouldPrint: true,
      shouldComplete: false,
      openDeliveryAfterSave: true,
      sendToDelivery: true,
    });
  };

  const getStoreLogoUrl = () =>
    absoluteUrl(pickFirstNonEmpty(vendorShop.shopLogo, vendorShop.shopImage));

  const getStoreDisplayName = () =>
    pickFirstNonEmpty(vendorShop.shopName, 'Grocery Store');

  const getStoreDisplayAddress = () => pickFirstNonEmpty(vendorShop.liveLocation, '');

  const getStoreDisplayPhone = () =>
    pickFirstNonEmpty(quickPrintSettings.phoneInvoice, vendorShop.number, '');

  const openInvoiceScreen = () => {
    if (!selectedOrder || !selectedEdit) return;

    const defaultState = getDefaultInvoiceState(selectedOrder, selectedEdit);
    setInvoiceState(defaultState);
    setCurrentScreen('invoice');
  };

  const handlePrintReceipt = () => {
    if (!selectedOrder || !selectedEdit) return;

    const summary = invoiceSummary || billSummary;

    const receiptWindow = window.open('', '_blank');

    if (!receiptWindow) {
      setMessage('Please allow popup to print receipt.');
      return;
    }

    const storeLogo = getStoreLogoUrl();
    const storeName = getStoreDisplayName();
    const storeAddress = getStoreDisplayAddress();
    const storePhone = getStoreDisplayPhone();

    const itemsHtml = (summary.items || [])
      .map((item) => {
        const qty = toNumber(item.quantity, 0);
        const rate = toNumber(item.rate, 0);
        const lineTotal = qty * rate;

        return `
          <tr>
            <td class="item-name">
              <div>${item.item_name || 'Product'}</div>
              ${item.unit_label ? `<div class="unit">${item.unit_label}</div>` : ''}
            </td>
            <td class="qty">${qty}</td>
            <td class="rate">${Math.round(rate)}</td>
            <td class="amt">${Math.round(lineTotal)}</td>
          </tr>
        `;
      })
      .join('');

    const logoHtml = storeLogo
      ? `<img src="${storeLogo}" class="shop-logo" />`
      : '';

    const html = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Receipt #${selectedOrder.id}</title>
          <meta charset="utf-8" />
          <meta name="viewport" content="width=device-width, initial-scale=1" />
          <style>
            * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            html, body {
              margin: 0;
              padding: 0;
              background: #fff;
              color: #000;
              font-family: Arial, sans-serif;
              width: 58mm;
              max-width: 58mm;
            }
            body { padding: 2mm; }
            .receipt { width: 54mm; margin: 0 auto; }
            .center { text-align: center; }
            .shop-logo { max-width: 18mm; max-height: 18mm; object-fit: contain; margin-bottom: 2mm; }
            .shop-name { font-size: 15px; font-weight: 800; line-height: 1.2; }
            .shop-line { font-size: 9px; line-height: 1.25; }
            .divider { border-top: 1px dashed #000; margin: 5px 0; }
            .meta-row { display: flex; justify-content: space-between; gap: 6px; font-size: 9px; margin: 2px 0; }
            .meta-row span:last-child { text-align: right; word-break: break-word; }
            table { width: 100%; border-collapse: collapse; table-layout: fixed; }
            th, td { font-size: 9px; padding: 3px 1px; vertical-align: top; }
            th { border-bottom: 1px dashed #000; }
            .item-name { width: 44%; word-break: break-word; }
            .unit { font-size: 8px; color: #555; margin-top: 1px; }
            .qty { width: 12%; text-align: center; }
            .rate { width: 20%; text-align: right; }
            .amt { width: 24%; text-align: right; }
            .summary-row { display: flex; justify-content: space-between; gap: 6px; font-size: 9px; padding: 2px 0; }
            .summary-row.total { font-size: 12px; font-weight: 800; border-top: 1px dashed #000; margin-top: 4px; padding-top: 5px; }
            .footer { text-align: center; font-size: 9px; margin-top: 8px; }
            .no-print { display: flex; gap: 6px; margin-top: 8px; }
            .no-print button { flex: 1; height: 34px; border: none; border-radius: 6px; font-weight: 700; }
            .print-btn { background: #2563eb; color: white; }
            .back-btn { background: #e5e7eb; color: #111; }
            @page { size: 58mm auto; margin: 0; }
            @media print {
              .no-print { display: none !important; }
              body { padding: 2mm; }
            }
          </style>
        </head>
        <body>
          <div class="receipt">
            <div class="center">
              ${logoHtml}
              <div class="shop-name">${storeName}</div>
              ${storeAddress ? `<div class="shop-line">${storeAddress}</div>` : ''}
              ${storePhone ? `<div class="shop-line">Ph: ${storePhone}</div>` : ''}
            </div>

            <div class="divider"></div>

            <div class="meta-row"><span>Bill No</span><span>${getInvoiceNumber(selectedOrder)}</span></div>
            <div class="meta-row"><span>Order</span><span>#${selectedOrder.id}</span></div>
            <div class="meta-row"><span>Date</span><span>${getInvoiceDateText(selectedOrder)}</span></div>
            <div class="meta-row"><span>Customer</span><span>${selectedOrder.customer_name_normalized || '-'}</span></div>
            <div class="meta-row"><span>Phone</span><span>${selectedOrder.customer_mobile || '-'}</span></div>
            <div class="meta-row"><span>Payment</span><span>${getPaymentLabel(invoiceState.paymentMethod)}</span></div>

            <div class="divider"></div>

            <table>
              <thead>
                <tr>
                  <th class="item-name">Item</th>
                  <th class="qty">Qty</th>
                  <th class="rate">Rate</th>
                  <th class="amt">Amt</th>
                </tr>
              </thead>
              <tbody>
                ${itemsHtml}
              </tbody>
            </table>

            <div class="divider"></div>

            <div class="summary-row"><span>Subtotal</span><strong>${Math.round(summary.subtotal)}</strong></div>
            <div class="summary-row"><span>Discount</span><strong>${Math.round(summary.discount)}</strong></div>
            <div class="summary-row"><span>Taxable</span><strong>${Math.round(summary.taxableAmount)}</strong></div>
            <div class="summary-row"><span>GST (${summary.gstPercent}%)</span><strong>${Math.round(summary.gstAmount)}</strong></div>
            <div class="summary-row"><span>Delivery</span><strong>${Math.round(summary.deliveryCharge)}</strong></div>
            <div class="summary-row"><span>Packing</span><strong>${Math.round(summary.packingCharge)}</strong></div>
            <div class="summary-row"><span>Round Off</span><strong>${Math.round(summary.roundOff)}</strong></div>
            <div class="summary-row total"><span>Total</span><strong>₹${Math.round(summary.finalTotal)}</strong></div>

            <div class="divider"></div>

            <div class="footer">Thank you! Visit Again.</div>

            <div class="no-print">
              <button class="back-btn" onclick="window.close()">Back</button>
              <button class="print-btn" onclick="window.print()">Print</button>
            </div>
          </div>

          <script>
            window.onafterprint = function () {
              try {
                if (window.opener) {
                  window.opener.postMessage({ type: 'GROCERY_RECEIPT_PRINT_DONE' }, '*');
                }
              } catch (e) {}
              setTimeout(function () { window.close(); }, 400);
            };
          </script>
        </body>
      </html>
    `;

    receiptWindow.document.open();
    receiptWindow.document.write(html);
    receiptWindow.document.close();
  };

  const changeReadyPreset = (nextPreset) => {
    setReadyReportPreset(nextPreset);

    if (nextPreset !== 'custom') {
      setShowReadyFilter(false);
    }
  };

  const applyCustomReadyFilter = () => {
    if (!readyStartDate || !readyEndDate) {
      setMessage('Ready report ke liye From aur To date select karein.');
      return;
    }

    if (new Date(readyStartDate) > new Date(readyEndDate)) {
      setMessage('From date, To date se badi nahi ho sakti.');
      return;
    }

    setReadyReportPreset('custom');
    setShowReadyFilter(false);
    fetchReadyReport({ silent: false });
  };

  const renderReadyFilterPopover = () => (
    <div
      className="gor-ready-filter-popover"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="gor-ready-filter-head">
        <strong>Ready Report</strong>
        <span>{getReadyReportLabel()}</span>
      </div>

      <div className="gor-ready-filter-list">
        {READY_REPORT_OPTIONS.map((option) => (
          <button
            key={option.key}
            type="button"
            className={readyReportPreset === option.key ? 'active' : ''}
            onClick={() => changeReadyPreset(option.key)}
          >
            {option.label}
          </button>
        ))}
      </div>

      {readyReportPreset === 'custom' ? (
        <div className="gor-ready-custom-box">
          <label>
            <span>From</span>
            <input
              type="date"
              value={readyStartDate}
              onChange={(e) => setReadyStartDate(e.target.value)}
            />
          </label>

          <label>
            <span>To</span>
            <input
              type="date"
              value={readyEndDate}
              onChange={(e) => setReadyEndDate(e.target.value)}
            />
          </label>

          <button type="button" onClick={applyCustomReadyFilter}>
            Apply
          </button>
        </div>
      ) : null}

      <div className="gor-ready-report-mini-list">
        {readyReport.loading ? (
          <div className="gor-ready-mini-empty">Loading...</div>
        ) : (readyReport.orders || []).length === 0 ? (
          <div className="gor-ready-mini-empty">Ready order nahi mila.</div>
        ) : (
          (readyReport.orders || []).slice(0, 8).map((item) => (
            <div
              className="gor-ready-mini-order"
              key={`${item.order_id || item.id}_${item.ready_at || item.created_at}`}
            >
              <div>
                <strong>#{item.order_id || item.id}</strong>
                <span>{item.customer_name || 'Customer'}</span>
              </div>
              <div>
                <b>{formatMoney(item.total_price || item.display_total || 0)}</b>
                <small>{formatDateTime(item.ready_at || item.created_at)}</small>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );

  const renderStatusCountRow = () => (
    <div className="gor-status-count-row">
      {[
        ['new', 'New', statusCounts.new],
        ['accepted', 'Accepted', statusCounts.accepted],
        ['packing', 'Packing', statusCounts.packing],
        ['ready', 'Ready', statusCounts.ready],
      ].map(([key, label, count]) => {
        const isReady = key === 'ready';

        return (
          <div
            key={key}
            className={`gor-status-count-chip-wrap ${isReady ? 'ready-wrap' : ''}`}
          >
            <button
              type="button"
              className={`gor-status-count-chip ${key} ${
                statusFilter === key ? 'active' : ''
              }`}
              onClick={() => {
                setActiveTab('active');
                setStatusFilter(statusFilter === key ? 'all' : key);
              }}
            >
              <span>
                {isReady && readyReportPreset !== 'today'
                  ? `${getReadyReportShortLabel()} ${label}`
                  : label}
              </span>
              <strong>{readyReport.loading && isReady ? '...' : count}</strong>
            </button>

            {isReady ? (
              <>
                <button
                  type="button"
                  className={`gor-ready-filter-icon ${showReadyFilter ? 'active' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowReadyFilter((prev) => !prev);
                  }}
                  title="Ready filter"
                >
                  ⚙️
                </button>

                {showReadyFilter ? renderReadyFilterPopover() : null}
              </>
            ) : null}
          </div>
        );
      })}
    </div>
  );

  const renderOrderCard = (order) => {
    const isActive = Number(selectedOrderId) === Number(order.id);
    const statusMeta = STATUS_FLOW[order.display_status] || STATUS_FLOW.new;
    const isDeliveryReady =
      order.order_mode_normalized === 'delivery' &&
      order.display_status === 'ready';

    return (
      <div
        key={order.id}
        className={`gor-order-card gor-order-card-compact gor-status-card-${order.display_status} ${
          isActive ? 'gor-order-card-selected' : ''
        }`}
        onClick={() => setSelectedOrderId(order.id)}
      >
        <div className="gor-order-status-dot" />

        <div className="gor-order-card-left">
          <div className="gor-order-row1">
            <div className="gor-order-id">#{order.id}</div>
            <div className="gor-order-time">{getTimeText(order.created_at)}</div>
            <div className={`gor-order-status-mini gor-status-${order.display_status}`}>
              {statusMeta.short}
            </div>
          </div>

          <div className="gor-order-customer">
            👤 {order.customer_name_normalized || 'Customer'}
          </div>

          <div className="gor-order-bottom-line gor-order-bottom-line-compact">
            <span>{formatMoney(order.display_total)}</span>
            <span>🛍️ {order.item_count || 0}</span>
            <span>{order.order_mode_normalized === 'delivery' ? 'Delivery' : 'Pickup'}</span>
            <span>{order.grocery_mode_normalized === 'wholesale' ? 'Wholesale' : 'Retail'}</span>
            <span>{getPaymentLabel(order.payment_method_normalized)}</span>
          </div>
        </div>

        <div className="gor-order-card-actions gor-order-card-actions-compact">
          <button
            type="button"
            className="gor-btn gor-btn-light gor-btn-compact"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedOrderId(order.id);
              setCurrentScreen('detail');
            }}
          >
            View
          </button>

          {!['completed', 'cancelled'].includes(order.display_status) ? (
            <button
              type="button"
              className={`gor-btn gor-btn-compact ${
                order.display_status === 'packing'
                  ? 'gor-btn-warning'
                  : 'gor-btn-primary'
              }`}
              onClick={(e) => {
                e.stopPropagation();

                if (isDeliveryReady) {
                  saveDeliveryDraftAndOpenDispatch(order);
                  return;
                }

                handleCardAction(order);
              }}
            >
              {isDeliveryReady ? 'Send Delivery' : statusMeta.action}
            </button>
          ) : (
            <button type="button" className="gor-btn gor-btn-compact gor-btn-success" disabled>
              Done
            </button>
          )}
        </div>
      </div>
    );
  };

  const renderListScreen = () => (
    <div className="gor-orders-page">
      <div className="gor-page-head gor-page-head-compact">
        <div>
          <h2>Orders Queue</h2>
          <p>Pickup order complete karein. Delivery order Ready hone ke baad rider assign karein.</p>
        </div>

        <button
          type="button"
          className="gor-btn gor-btn-primary"
          onClick={openDeliveryDispatch}
        >
          🛵 Delivery Management
        </button>
      </div>

      <div className="gor-sticky-controls">
        <div className="gor-list-top-row">
          <input
            type="text"
            className="gor-search-input"
            placeholder="Search order / customer / mobile"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          <select
            className="gor-filter-select"
            value={groceryTypeFilter}
            onChange={(e) => setGroceryTypeFilter(e.target.value)}
          >
            <option value="all">Retail + Wholesale</option>
            <option value="retail">Retail</option>
            <option value="wholesale">Wholesale</option>
          </select>

          <button
            type="button"
            className="gor-btn gor-btn-primary gor-refresh-btn"
            onClick={() => {
              fetchOrders({ silent: false });
              fetchReadyReport({ silent: false });
            }}
          >
            Refresh
          </button>
        </div>

        {renderStatusCountRow()}

        <div className="gor-tabs-wrap">
          <button
            type="button"
            className={`gor-tab-btn ${activeTab === 'active' ? 'active' : ''}`}
            onClick={() => setActiveTab('active')}
          >
            Today Active ({activeOrders.length})
          </button>

          <button
            type="button"
            className={`gor-tab-btn ${activeTab === 'history' ? 'active' : ''}`}
            onClick={() => setActiveTab('history')}
          >
            History
          </button>
        </div>
      </div>

      <div className="gor-list-layout">
        <div className="gor-list-pane">
          <div className="gor-orders-list">
            {initialLoading ? (
              <div className="gor-empty-box">Loading orders...</div>
            ) : visibleOrders.length === 0 ? (
              <div className="gor-empty-box">No orders found.</div>
            ) : (
              visibleOrders.map(renderOrderCard)
            )}
          </div>
        </div>

        <div className="gor-preview-pane">
          {selectedOrder ? (
            <div className="gor-preview-card">
              <div className="gor-preview-head">
                <div>
                  <span className="gor-preview-kicker">Selected Grocery Order</span>
                  <h3>Order #{selectedOrder.id}</h3>
                  <p>{formatDateTime(selectedOrder.created_at)}</p>
                </div>

                <span className={`gor-order-status-mini gor-status-${selectedOrder.display_status}`}>
                  {STATUS_FLOW[selectedOrder.display_status]?.label || 'New'}
                </span>
              </div>

              <div className="gor-preview-progress-card">
                <div className="gor-preview-progress">
                  {['new', 'accepted', 'packing', 'ready', 'completed'].map((statusKey, index) => {
                    const activeIndex = ['new', 'accepted', 'packing', 'ready', 'completed'].indexOf(
                      selectedOrder.display_status
                    );
                    const completed = index <= Math.max(activeIndex, 0);

                    return (
                      <div
                        key={statusKey}
                        className={`gor-preview-progress-step ${completed ? 'active' : ''}`}
                      >
                        <span>{completed ? '✓' : index + 1}</span>
                        <small>{STATUS_FLOW[statusKey]?.label || statusKey}</small>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="gor-preview-customer-card">
                <div className="gor-preview-avatar">
                  {String(selectedOrder.customer_name_normalized || 'C').charAt(0).toUpperCase()}
                </div>

                <div>
                  <strong>{selectedOrder.customer_name_normalized || 'Customer'}</strong>
                  <span>{selectedOrder.customer_mobile || '-'}</span>
                </div>

                <div className="gor-preview-mode-stack">
                  <b className={selectedOrder.order_mode_normalized === 'delivery' ? 'delivery' : 'pickup'}>
                    {selectedOrder.order_mode_normalized === 'delivery' ? '🛵 Delivery' : '🛍️ Pickup'}
                  </b>
                  <small>
                    {selectedOrder.grocery_mode_normalized === 'wholesale' ? 'Wholesale' : 'Retail'}
                  </small>
                </div>
              </div>

              {selectedOrder.order_mode_normalized === 'delivery' ? (
                <div className="gor-preview-delivery-box">
                  <div>
                    <span>Delivery Address</span>
                    <strong>{selectedOrder.full_address || 'Address missing'}</strong>
                  </div>

                  <div className="gor-preview-delivery-grid">
                    <div>
                      <span>Distance</span>
                      <strong>
                        {toNumber(selectedOrder.delivery_distance_km, 0) > 0
                          ? `${toNumber(selectedOrder.delivery_distance_km, 0).toFixed(2)} KM`
                          : '-'}
                      </strong>
                    </div>

                    <div>
                      <span>GPS Accuracy</span>
                      <strong>
                        {toNumber(selectedOrder.location_accuracy_meters, 0) > 0
                          ? `${toNumber(selectedOrder.location_accuracy_meters, 0)} m`
                          : '-'}
                      </strong>
                    </div>
                  </div>

                  {getCustomerMapUrl(selectedOrder) ? (
                    <a
                      className="gor-btn gor-btn-light"
                      href={getCustomerMapUrl(selectedOrder)}
                      target="_blank"
                      rel="noreferrer"
                    >
                      📍 Open Customer Location
                    </a>
                  ) : null}
                </div>
              ) : null}

              <div className="gor-preview-summary-grid">
                <div>
                  <span>Items</span>
                  <strong>{selectedOrder.item_count || 0}</strong>
                </div>

                <div>
                  <span>Payment</span>
                  <strong>{getPaymentLabel(selectedOrder.payment_method_normalized)}</strong>
                </div>

                <div>
                  <span>Total</span>
                  <strong>{formatMoney(selectedOrder.display_total)}</strong>
                </div>
              </div>

              <div className="gor-preview-action-grid">
                <button
                  type="button"
                  className="gor-btn gor-btn-light"
                  onClick={() => setCurrentScreen('detail')}
                >
                  View Details
                </button>

                {selectedEdit ? (
                  <button
                    type="button"
                    className="gor-btn gor-btn-success"
                    onClick={openInvoiceScreen}
                  >
                    Open Invoice
                  </button>
                ) : null}

                {!['completed', 'cancelled'].includes(selectedOrder.display_status) ? (
                  <button
                    type="button"
                    className="gor-btn gor-btn-primary"
                    onClick={() => {
                      if (
                        selectedOrder.order_mode_normalized === 'delivery' &&
                        selectedOrder.display_status === 'ready'
                      ) {
                        saveDeliveryDraftAndOpenDispatch(selectedOrder);
                        return;
                      }

                      handleCardAction(selectedOrder);
                    }}
                  >
                    {selectedOrder.order_mode_normalized === 'delivery' &&
                    selectedOrder.display_status === 'ready'
                      ? 'Send Delivery'
                      : STATUS_FLOW[selectedOrder.display_status]?.action || 'Update'}
                  </button>
                ) : null}
              </div>
            </div>
          ) : (
            <div className="gor-empty-box">
              Left side se order select karein. Details yahan show hongi.
            </div>
          )}
        </div>
      </div>
    </div>
  );


  const renderDetailScreen = () => {
    if (!selectedOrder || !selectedEdit) {
      return (
        <div className="gor-view-page">
          <div className="gor-empty-box">Order details not found.</div>
        </div>
      );
    }

    return (
      <div className="gor-view-page">
        <div className="gor-simple-header">
          <button type="button" className="gor-back-icon" onClick={() => setCurrentScreen('list')}>
            ←
          </button>

          <div>
            <h2>{isCompletedOrder ? 'View Order' : 'Edit Order'}</h2>
            <p>Order #{selectedOrder.id}</p>
          </div>
        </div>

        <div className="gor-view-top-card gor-view-top-card-simple">
          <div className="gor-view-top-left">
            <div className="gor-view-avatar">
              {String(selectedOrder.customer_name_normalized || 'C').charAt(0).toUpperCase()}
            </div>

            <div className="gor-view-customer-info">
              <div className="gor-view-customer-name">
                {selectedOrder.customer_name_normalized}
              </div>
              <div className="gor-view-customer-phone">
                {selectedOrder.customer_mobile || '-'}
              </div>
            </div>
          </div>

          <div className="gor-view-simple-stats">
            <div>
              <span>Mode</span>
              <strong>{selectedOrder.order_mode_normalized === 'delivery' ? 'Delivery' : 'Pickup'}</strong>
            </div>

            <div>
              <span>Payment</span>
              <strong>{getPaymentLabel(selectedOrder.payment_method_normalized)}</strong>
            </div>

            <div>
              <span>Total</span>
              <strong>{formatMoney(billSummary.finalTotal)}</strong>
            </div>
          </div>
        </div>

        {isCompletedOrder ? (
          <div className="gor-completed-note">
            Completed order view only hai. Aap sirf invoice/print kar sakte hain.
          </div>
        ) : null}

        {!isCompletedOrder && selectedOrder.order_mode_normalized === 'delivery' ? (
          <div className="gor-completed-note">
            🛵 Delivery order direct complete nahi hoga. Ready status ke baad invoice save karein
            aur Delivery Management me delivery boy assign karein. Final completion customer token
            verify hone ke baad hogi.

            <div className="gor-bottom-action-bar" style={{ marginTop: '12px' }}>
              {getCustomerMapUrl(selectedOrder) ? (
                <a
                  className="gor-btn gor-btn-light"
                  href={getCustomerMapUrl(selectedOrder)}
                  target="_blank"
                  rel="noreferrer"
                >
                  📍 Open Customer Location
                </a>
              ) : null}

              <button
                type="button"
                className="gor-btn gor-btn-primary"
                onClick={openDeliveryDispatch}
              >
                🛵 Open Delivery Management
              </button>
            </div>
          </div>
        ) : null}

        <div className="gor-edit-layout">
          <div className="gor-edit-main">
            <div className="gor-section-head">
              <h3>Order Items</h3>
            </div>

            <div className="gor-view-table">
              <div className="gor-view-table-head">
                <div>Product</div>
                <div>Qty</div>
                <div>Price</div>
                <div>Total</div>
                <div>Action</div>
              </div>

              {(selectedEdit.items || []).map((item, index) => {
                const qty = toNumber(item.quantity, 0);
                const rate = toNumber(item.rate, 0);
                const lineTotal = item.removed ? 0 : qty * rate;

                return (
                  <div
                    key={`${item.id}_${index}`}
                    className={`gor-view-table-row ${item.removed ? 'gor-row-removed' : ''}`}
                  >
                    <div className="gor-view-product-cell">
                      <div className="gor-item-main">
                        <div className="gor-item-thumb">
                          {item.image ? (
                            <img
                              src={absoluteUrl(item.image)}
                              alt={item.item_name}
                              className="gor-item-image"
                            />
                          ) : (
                            <div className="gor-item-image gor-item-image-placeholder">📦</div>
                          )}
                        </div>

                        <div className="gor-item-meta">
                          <div className="gor-item-name">{item.item_name}</div>
                          <div className="gor-item-unit">{item.unit_label || 'Unit'}</div>
                        </div>
                      </div>
                    </div>

                    <div className="gor-qty-stepper">
                      <button
                        type="button"
                        onClick={() => changeQuantityByStep(selectedOrder.id, index, -1)}
                        disabled={isCompletedOrder}
                      >
                        -
                      </button>

                      <input
                        type="number"
                        min="0"
                        value={item.quantity}
                        onChange={(e) =>
                          changeItemField(selectedOrder.id, index, 'quantity', e.target.value)
                        }
                        disabled={isCompletedOrder}
                      />

                      <button
                        type="button"
                        onClick={() => changeQuantityByStep(selectedOrder.id, index, 1)}
                        disabled={isCompletedOrder}
                      >
                        +
                      </button>
                    </div>

                    <div className="gor-view-input-box">
                      <input
                        type="number"
                        min="0"
                        value={item.rate}
                        onChange={(e) =>
                          changeItemField(selectedOrder.id, index, 'rate', e.target.value)
                        }
                        disabled={isCompletedOrder}
                      />
                    </div>

                    <div className="gor-view-total-cell">
                      {formatMoney(lineTotal)}
                    </div>

                    <button
                      type="button"
                      className="gor-small-remove"
                      onClick={() => toggleRemoveItem(selectedOrder.id, index)}
                      disabled={isCompletedOrder}
                    >
                      {item.removed ? 'Undo' : '×'}
                    </button>
                  </div>
                );
              })}
            </div>

            <div className="gor-manual-items-box">
              <div className="gor-section-head">
                <h3>Manual Product</h3>

                <button
                  type="button"
                  className="gor-btn gor-btn-outline gor-small-btn"
                  onClick={() => addManualItem(selectedOrder.id)}
                  disabled={isCompletedOrder}
                >
                  + Add Product
                </button>
              </div>

              {(selectedEdit.manualItems || []).length === 0 ? (
                <div className="gor-empty-mini">Manual product add nahi hai.</div>
              ) : (
                (selectedEdit.manualItems || []).map((item, index) => {
                  const qty = toNumber(item.quantity, 0);
                  const rate = toNumber(item.rate, 0);
                  const lineTotal = qty * rate;

                  return (
                    <div key={item.id || index} className="gor-manual-product-card">
                      <div className="gor-manual-product-grid">
                        <input
                          type="text"
                          placeholder="Product name"
                          value={item.item_name}
                          onChange={(e) =>
                            changeManualItemField(
                              selectedOrder.id,
                              index,
                              'item_name',
                              e.target.value
                            )
                          }
                          disabled={isCompletedOrder}
                        />

                        <div className="gor-qty-stepper">
                          <button
                            type="button"
                            onClick={() =>
                              changeManualQuantityByStep(selectedOrder.id, index, -1)
                            }
                            disabled={isCompletedOrder}
                          >
                            -
                          </button>

                          <input
                            type="number"
                            min="0"
                            value={item.quantity}
                            onChange={(e) =>
                              changeManualItemField(
                                selectedOrder.id,
                                index,
                                'quantity',
                                e.target.value
                              )
                            }
                            disabled={isCompletedOrder}
                          />

                          <button
                            type="button"
                            onClick={() =>
                              changeManualQuantityByStep(selectedOrder.id, index, 1)
                            }
                            disabled={isCompletedOrder}
                          >
                            +
                          </button>
                        </div>

                        <input
                          type="number"
                          placeholder="Rate"
                          value={item.rate}
                          onChange={(e) =>
                            changeManualItemField(
                              selectedOrder.id,
                              index,
                              'rate',
                              e.target.value
                            )
                          }
                          disabled={isCompletedOrder}
                        />

                        <strong>{formatMoney(lineTotal)}</strong>

                        <button
                          type="button"
                          className="gor-btn gor-btn-danger gor-small-btn"
                          onClick={() => removeManualItem(selectedOrder.id, index)}
                          disabled={isCompletedOrder}
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          <div className="gor-edit-side">
            <div className="gor-pricing-card">
              <h3>Pricing Summary</h3>

              <div className="gor-price-line">
                <span>Subtotal</span>
                <strong>{formatMoney(billSummary.subtotal)}</strong>
              </div>

              <div className="gor-price-line">
                <span>Delivery Charge</span>
                <input
                  type="number"
                  value={selectedEdit.deliveryCharge}
                  onChange={(e) =>
                    changeOrderField(selectedOrder.id, 'deliveryCharge', e.target.value)
                  }
                  disabled={isCompletedOrder}
                />
              </div>

              <div className="gor-price-line">
                <span>Packing Charge</span>
                <input
                  type="number"
                  value={selectedEdit.packingCharge}
                  onChange={(e) =>
                    changeOrderField(selectedOrder.id, 'packingCharge', e.target.value)
                  }
                  disabled={isCompletedOrder}
                />
              </div>

              <div className="gor-price-line">
                <span>Discount</span>
                <input
                  type="number"
                  value={selectedEdit.manualDiscount}
                  onChange={(e) =>
                    changeOrderField(selectedOrder.id, 'manualDiscount', e.target.value)
                  }
                  disabled={isCompletedOrder}
                />
              </div>

              <div className="gor-price-line">
                <span>Taxable</span>
                <strong>{formatMoney(billSummary.taxableAmount)}</strong>
              </div>

              <div className="gor-price-line">
                <span>GST %</span>
                <input
                  type="number"
                  value={selectedEdit.gstPercent}
                  onChange={(e) =>
                    changeOrderField(selectedOrder.id, 'gstPercent', e.target.value)
                  }
                  disabled={isCompletedOrder}
                />
              </div>

              <div className="gor-price-line">
                <span>GST Amount</span>
                <strong>{formatMoney(billSummary.gstAmount)}</strong>
              </div>

              <div className="gor-price-line">
                <span>Round Off</span>
                <input
                  type="number"
                  value={selectedEdit.roundOff}
                  onChange={(e) =>
                    changeOrderField(selectedOrder.id, 'roundOff', e.target.value)
                  }
                  disabled={isCompletedOrder}
                />
              </div>

              <div className="gor-price-line final">
                <span>Final Total</span>
                <strong>{formatMoney(billSummary.finalTotal)}</strong>
              </div>
            </div>

            <div className="gor-notes-card">
              <h3>Notes / Summary</h3>
              <textarea
                placeholder="Short note optional..."
                value={selectedEdit.summaryText || ''}
                onChange={(e) =>
                  changeOrderField(selectedOrder.id, 'summaryText', e.target.value)
                }
                disabled={isCompletedOrder}
              />
            </div>
          </div>
        </div>

        <div className="gor-bottom-action-bar">
          <button
            type="button"
            className="gor-btn gor-btn-light"
            onClick={() => setCurrentScreen('list')}
          >
            Back
          </button>

          <button
            type="button"
            className="gor-btn gor-btn-primary"
            onClick={handleUpdateOrderEdits}
            disabled={isCompletedOrder}
          >
            Save Changes
          </button>

          <button
            type="button"
            className="gor-btn gor-btn-success"
            onClick={openInvoiceScreen}
          >
            Open Invoice
          </button>
        </div>
      </div>
    );
  };

  const renderInvoiceScreen = () => {
    if (!selectedOrder || !selectedEdit) {
      return (
        <div className="gor-invoice-page">
          <div className="gor-empty-box">Invoice data not found.</div>
        </div>
      );
    }

    return (
      <div className="gor-invoice-page">
        <div className="gor-simple-header">
          <button type="button" className="gor-back-icon" onClick={() => setCurrentScreen('detail')}>
            ←
          </button>

          <div>
            <h2>Invoice & GST</h2>
            <p>GST calculation step by step</p>
          </div>
        </div>

        <div className="gor-invoice-card">
          <div className="gor-invoice-topbar">
            <div className="gor-invoice-store">
              {getStoreLogoUrl() ? (
                <img
                  src={getStoreLogoUrl()}
                  alt="Shop Logo"
                  className="gor-invoice-store-logo"
                />
              ) : (
                <div className="gor-invoice-store-icon">🛒</div>
              )}

              <div>
                <div className="gor-invoice-store-name">{getStoreDisplayName()}</div>
                {getStoreDisplayAddress() ? (
                  <div className="gor-invoice-store-address">{getStoreDisplayAddress()}</div>
                ) : null}
                {getStoreDisplayPhone() ? (
                  <div className="gor-invoice-store-phone">{getStoreDisplayPhone()}</div>
                ) : null}
              </div>
            </div>
          </div>

          <div className="gor-invoice-meta-grid">
            <div>
              <span>Invoice #</span>
              <strong>{getInvoiceNumber(selectedOrder)}</strong>
            </div>

            <div>
              <span>Order ID</span>
              <strong>#{selectedOrder.id}</strong>
            </div>

            <div>
              <span>Customer</span>
              <strong>{selectedOrder.customer_name_normalized}</strong>
              <small>{selectedOrder.customer_mobile || '-'}</small>
            </div>

            <div>
              <span>Date</span>
              <strong>{getInvoiceDateText(selectedOrder)}</strong>
            </div>
          </div>

          <div className="gor-invoice-layout">
            <div className="gor-invoice-main">
              <div className="gor-invoice-table-card">
                <div className="gor-invoice-table-head">
                  <div>Item</div>
                  <div>Qty</div>
                  <div>Rate</div>
                  <div>Amount</div>
                </div>

                {invoiceSummary.items.map((item, index) => {
                  const qty = toNumber(item.quantity, 0);
                  const rate = toNumber(item.rate, 0);
                  const lineTotal = qty * rate;

                  return (
                    <div className="gor-invoice-table-row" key={`${item.id}_${index}`}>
                      <div className="gor-invoice-item-cell">
                        <strong>{item.item_name}</strong>
                        <span>{item.unit_label || ''}</span>
                      </div>
                      <div>{qty}</div>
                      <div>{formatMoney(rate)}</div>
                      <div>{formatMoney(lineTotal)}</div>
                    </div>
                  );
                })}
              </div>

              <div className="gor-payment-card">
                <h3>Payment</h3>

                <div className="gor-payment-status-row">
                  {['paid', 'unpaid', 'partial'].map((status) => (
                    <button
                      key={status}
                      type="button"
                      className={`gor-payment-status-btn ${
                        invoiceState.paymentStatus === status ? 'active' : ''
                      }`}
                      onClick={() =>
                        setInvoiceState((prev) => ({
                          ...prev,
                          paymentStatus: status,
                          paymentMethod:
                            status === 'unpaid'
                              ? selectedOrder.order_mode_normalized === 'delivery'
                                ? 'cod'
                                : 'credit'
                              : prev.paymentMethod,
                          amountPaid: status === 'partial' ? prev.amountPaid : '',
                        }))
                      }
                    >
                      {status === 'paid' ? 'Paid' : status === 'unpaid' ? 'Unpaid' : 'Partial'}
                    </button>
                  ))}
                </div>

                <div className="gor-payment-grid">
                  <div>
                    <label>Payment Method</label>
                    <select
                      value={invoiceState.paymentMethod}
                      onChange={(e) =>
                        setInvoiceState((prev) => ({
                          ...prev,
                          paymentMethod: e.target.value,
                        }))
                      }
                      disabled={
                        invoiceState.paymentStatus === 'unpaid' &&
                        selectedOrder.order_mode_normalized !== 'delivery'
                      }
                    >
                      <option value="cash">Cash</option>
                      <option value="upi">UPI</option>
                      <option value="credit">Credit</option>
                      <option value="card">Card</option>
                      <option value="online">Online</option>
                      <option value="cod">COD</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  {invoiceState.paymentStatus === 'partial' ? (
                    <div>
                      <label>Partial Paid Amount</label>
                      <input
                        type="number"
                        value={invoiceState.amountPaid}
                        onChange={(e) =>
                          setInvoiceState((prev) => ({
                            ...prev,
                            amountPaid: e.target.value,
                          }))
                        }
                        placeholder="Paid amount"
                      />
                    </div>
                  ) : null}
                </div>
              </div>
            </div>

            <div className="gor-invoice-side">
              <div className="gor-invoice-summary-box">
                <div className="gor-invoice-summary-row">
                  <span>Subtotal</span>
                  <strong>{formatMoney(invoiceSummary.subtotal)}</strong>
                </div>

                <div className="gor-invoice-discount-row">
                  <span>Discount</span>
                  <input
                    type="number"
                    value={invoiceState.discountValue}
                    onChange={(e) =>
                      setInvoiceState((prev) => ({
                        ...prev,
                        discountValue: e.target.value,
                      }))
                    }
                  />
                </div>

                <div className="gor-invoice-summary-row">
                  <span>Taxable Amount</span>
                  <strong>{formatMoney(invoiceSummary.taxableAmount)}</strong>
                </div>

                <div className="gor-invoice-summary-row">
                  <span>GST %</span>
                  <strong>{invoiceSummary.gstPercent}%</strong>
                </div>

                <div className="gor-invoice-summary-row">
                  <span>GST Amount</span>
                  <strong>{formatMoney(invoiceSummary.gstAmount)}</strong>
                </div>

                <div className="gor-invoice-summary-row">
                  <span>Delivery Charge</span>
                  <strong>{formatMoney(invoiceSummary.deliveryCharge)}</strong>
                </div>

                <div className="gor-invoice-summary-row">
                  <span>Packing Charge</span>
                  <strong>{formatMoney(invoiceSummary.packingCharge)}</strong>
                </div>

                <div className="gor-invoice-summary-row">
                  <span>Round Off</span>
                  <strong>{formatMoney(invoiceSummary.roundOff)}</strong>
                </div>

                <div className="gor-invoice-summary-row gor-invoice-final-row">
                  <span>Final Total</span>
                  <strong>{formatMoney(invoiceSummary.finalTotal)}</strong>
                </div>
              </div>

              <div className="gor-formula-card">
                <strong>GST Amount</strong>
                <span>Taxable Amount × GST %</span>
                <p>
                  {formatMoney(invoiceSummary.gstAmount)} ={' '}
                  {formatMoney(invoiceSummary.taxableAmount)} × {invoiceSummary.gstPercent}%
                </p>
              </div>

              <div className="gor-formula-card">
                <strong>Taxable Amount</strong>
                <span>Subtotal - Discount</span>
                <p>
                  {formatMoney(invoiceSummary.taxableAmount)} ={' '}
                  {formatMoney(invoiceSummary.subtotal)} - {formatMoney(invoiceSummary.discount)}
                </p>
              </div>
            </div>
          </div>

          <div className="gor-bottom-action-bar gor-bottom-action-bar-four">
            <button
              type="button"
              className="gor-btn gor-btn-light"
              onClick={() => setCurrentScreen('detail')}
            >
              Back
            </button>

            <button
              type="button"
              className="gor-btn gor-btn-primary"
              onClick={() =>
                handleSaveInvoice({
                  shouldPrint: false,
                  shouldComplete:
                    selectedOrder.order_mode_normalized !== 'delivery',
                })
              }
            >
              Save Invoice
            </button>

            <button
              type="button"
              className="gor-btn gor-btn-success"
              onClick={handlePrintReceipt}
            >
              Print Bill
            </button>

            <button
              type="button"
              className="gor-btn gor-btn-success"
              onClick={
                selectedOrder.order_mode_normalized === 'delivery'
                  ? handleSavePrintSendDelivery
                  : handleSavePrintComplete
              }
              disabled={isCompletedOrder}
            >
              {selectedOrder.order_mode_normalized === 'delivery'
                ? 'Save + Print + Send Delivery'
                : 'Save + Print + Complete'}
            </button>
          </div>
        </div>
      </div>
    );
  };


  const renderDesktopSidebar = () => (
    <aside className="gor-app-sidebar" aria-label="Grocery navigation">
      <div className="gor-side-brand">
        <span className="gor-side-brand-icon">🛒</span>
        <div>
          <strong>{getStoreDisplayName()}</strong>
          <small>Grocery Vendor</small>
        </div>
      </div>

      <nav className="gor-side-nav">
        <button
          type="button"
          className={sidePanel === 'orders' && activeTab === 'active' && statusFilter === 'all' ? 'active' : ''}
          onClick={() => openOrdersPanel({ tab: 'active', status: 'all' })}
        >
          <span>⌂</span>
          Dashboard
        </button>

        <button
          type="button"
          className={sidePanel === 'orders' && activeTab === 'active' ? 'active' : ''}
          onClick={() => openOrdersPanel({ tab: 'active', status: 'all' })}
        >
          <span>🧾</span>
          Orders
        </button>

        <button
          type="button"
          className={sidePanel === 'orders' && statusFilter === 'packing' ? 'active' : ''}
          onClick={() => openOrdersPanel({ tab: 'active', status: 'packing' })}
        >
          <span>📦</span>
          Packing
        </button>

        <button
          type="button"
          className={sidePanel === 'orders' && currentScreen === 'invoice' ? 'active' : ''}
          onClick={openInvoicePanel}
        >
          <span>📄</span>
          Invoice
        </button>

        <button
          type="button"
          className={sidePanel === 'delivery' ? 'active' : ''}
          onClick={openDeliveryDispatch}
        >
          <span>🛵</span>
          Delivery
        </button>

        <button
          type="button"
          className={sidePanel === 'orders' && activeTab === 'history' ? 'active' : ''}
          onClick={() => openOrdersPanel({ tab: 'history', status: 'all' })}
        >
          <span>🕘</span>
          History
        </button>

        <button
          type="button"
          className={sidePanel === 'settings' ? 'active' : ''}
          onClick={() => setSidePanel('settings')}
        >
          <span>⚙️</span>
          Settings
        </button>
      </nav>

      <button
        type="button"
        className="gor-side-logout"
        onClick={() => {
          localStorage.removeItem('token');
          window.location.href = '/login';
        }}
      >
        <span>↪</span>
        Logout
      </button>
    </aside>
  );

  const renderMobileBottomNav = () => (
    <nav className="gor-mobile-bottom-nav" aria-label="Grocery quick navigation">
      <button
        type="button"
        className={sidePanel === 'orders' && activeTab === 'active' && statusFilter === 'all' ? 'active' : ''}
        onClick={() => openOrdersPanel({ tab: 'active', status: 'all' })}
      >
        <span>🧾</span>
        Orders
      </button>

      <button
        type="button"
        className={sidePanel === 'orders' && statusFilter === 'packing' ? 'active' : ''}
        onClick={() => openOrdersPanel({ tab: 'active', status: 'packing' })}
      >
        <span>📦</span>
        Packing
      </button>

      <button
        type="button"
        className={sidePanel === 'delivery' ? 'active' : ''}
        onClick={openDeliveryDispatch}
      >
        <span>🛵</span>
        Delivery
      </button>

      <button
        type="button"
        className={sidePanel === 'orders' && activeTab === 'history' ? 'active' : ''}
        onClick={() => openOrdersPanel({ tab: 'history', status: 'all' })}
      >
        <span>🕘</span>
        History
      </button>

      <button
        type="button"
        className={sidePanel === 'orders' && currentScreen === 'invoice' ? 'active' : ''}
        onClick={openInvoicePanel}
      >
        <span>📄</span>
        Invoice
      </button>
    </nav>
  );

  const renderSettingsPanel = () => (
    <section className="gor-settings-panel">
      <div className="gor-panel-title-row">
        <div>
          <h2>Grocery Settings</h2>
          <p>Invoice phone number aur manual item preference yahan manage karein.</p>
        </div>
        <button type="button" onClick={() => openOrdersPanel({ tab: 'active', status: 'all' })}>
          ← Orders
        </button>
      </div>

      <div className="gor-settings-grid">
        <label>
          Invoice Phone Number
          <input
            value={quickPrintSettings.phoneInvoice || ''}
            onChange={(e) =>
              setQuickPrintSettings((prev) => ({
                ...prev,
                phoneInvoice: e.target.value,
              }))
            }
            placeholder="Invoice me show hone wala phone"
          />
        </label>

        <label className="gor-check-setting">
          <input
            type="checkbox"
            checked={Boolean(uiSettings.allowManualItems)}
            onChange={(e) =>
              setUiSettings((prev) => ({
                ...prev,
                allowManualItems: e.target.checked,
              }))
            }
          />
          Invoice me manual products allow karein
        </label>
      </div>

      <div className="gor-settings-note">
        Settings browser me locally save hoti hain. Delivery panel sidebar ke andar same page par open hota hai.
      </div>
    </section>
  );

  const renderMainPanel = () => {
    if (sidePanel === 'delivery') {
      return (
        <DeliveryDispatch
          embedded
          externalVendorId={vendorId}
          externalVendorType={vendorType || 'grocery'}
          onBack={() => openOrdersPanel({ tab: 'active', status: 'all' })}
        />
      );
    }

    if (sidePanel === 'settings') {
      return renderSettingsPanel();
    }

    return (
      <>
        {currentScreen === 'list' && renderListScreen()}
        {currentScreen === 'detail' && renderDetailScreen()}
        {currentScreen === 'invoice' && renderInvoiceScreen()}
      </>
    );
  };

  return (
    <>
      <Dashboard />

      <div
        className="page-below-navbar gor-page-shell"
        onClick={() => {
          if (showReadyFilter) setShowReadyFilter(false);
        }}
      >
        <div className="gor-app-layout">
          {renderDesktopSidebar()}

          <main className="gor-main-content">
            <div className="gor-container">
              {message ? <div className="gor-top-message">{message}</div> : null}
              {renderMainPanel()}
            </div>
          </main>
        </div>

        {renderMobileBottomNav()}
      </div>
    </>
  );
};

export default GroceryOrdersReceived;
